import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { Button, Progress, Skeleton } from '@pagespeedify/ui';
import { CloudLightning, Loader2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../lib/apiClient';
import { ReportUsageResponse } from '../../types/reports';
import { Link } from 'react-router-dom';

interface FieldDataFetcherProps {
    monitoringId: string;
    websiteUrl: string;
    strategy: 'mobile' | 'desktop';
    hasFieldData?: boolean;
    initialFieldDataCount?: number;
    onComplete?: () => void;
    /** Compact mode - shows just a small refresh icon button */
    compact?: boolean;
}

interface MonitoringDetail {
    success: boolean;
    data?: {
        id: string;
        additional_metrics?: {
            field_data_history?: Array<{
                fetched_at: string;
                strategy: string;
                performance?: { score: number };
            }>;
        };
    };
}

export const FieldDataFetcher: React.FC<FieldDataFetcherProps> = ({
    monitoringId,
    websiteUrl,
    strategy,
    hasFieldData = false,
    initialFieldDataCount = 0,
    onComplete,
    compact = false,
}) => {
    const apiClient = useApi();
    const queryClient = useQueryClient();

    // Fetch reports usage for canCreate check and usage display
    const { data: reportUsage, isLoading: isUsageLoading } = useQuery<ReportUsageResponse>({
        queryKey: ['reportUsage'],
        queryFn: async () => {
            if (!apiClient) throw new Error('API client not available');
            const response = await apiClient.get<{ success: boolean; usage: ReportUsageResponse }>('/reports/usage');
            if (!response.success || !response.usage) throw new Error('Failed to fetch usage data.');
            return response.usage;
        },
        staleTime: 30000, // Cache for 30 seconds
        retry: 1,
        retryDelay: 5000,
        refetchOnWindowFocus: false,
    });

    // Track the field data fetch state
    const [isPolling, setIsPolling] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);

    // Use refs to avoid closure issues in polling callback
    const pollCountRef = useRef(0);
    const consecutiveErrorsRef = useRef(0);
    const initialCountRef = useRef<number>(initialFieldDataCount);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isMountedRef = useRef(true);
    const isPollingRef = useRef(false);

    // Polling configuration
    const BASE_INTERVAL = 5000; // 5 seconds base
    const MAX_INTERVAL = 30000; // 30 seconds max with backoff
    const MAX_POLL_ATTEMPTS = 24; // 2 minutes with base interval
    const MAX_CONSECUTIVE_ERRORS = 3;

    // Cleanup on unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            isPollingRef.current = false;
            if (pollingIntervalRef.current) {
                clearTimeout(pollingIntervalRef.current);
            }
        };
    }, []);

    // Calculate polling interval with exponential backoff on errors
    const getPollingInterval = useCallback(() => {
        if (consecutiveErrorsRef.current === 0) return BASE_INTERVAL;
        // Exponential backoff: 5s, 10s, 20s, capped at 30s
        return Math.min(BASE_INTERVAL * Math.pow(2, consecutiveErrorsRef.current), MAX_INTERVAL);
    }, []);

    // Mutation to initiate field data fetch
    const initiateMutation = useMutation({
        mutationFn: async () => {
            console.log('[FieldDataFetcher] Initiating field data request', {
                monitoringId,
                websiteUrl,
                strategy,
            });

            const response = await apiClient.post<{ reportId: string; success: boolean }>('/reports', {
                page_url: websiteUrl,
                website_url: websiteUrl,
                strategy,
                source: 'monitoring',
                monitoring_id: monitoringId,
            });

            console.log('[FieldDataFetcher] POST /reports response:', response);
            return response;
        },
        onSuccess: () => {
            // Store initial count and start polling
            initialCountRef.current = initialFieldDataCount;
            pollCountRef.current = 0;
            consecutiveErrorsRef.current = 0;
            isPollingRef.current = true;
            setIsPolling(true);
            setProgress(10);
            setError(null);
            toast.info('Field data scan started! This may take up to a minute...');
            // Start the polling loop after a delay
            pollingIntervalRef.current = setTimeout(pollMonitoringData, 3000);
        },
        onError: (error: any) => {
            const message = error?.response?.data?.message || error?.message || 'Failed to initiate field data scan';
            console.error('[FieldDataFetcher] Mutation error:', error);
            toast.error(message);
            setError(message);
        },
    });

    // Poll for monitoring data - uses refs to avoid closure issues
    const pollMonitoringData = useCallback(async () => {
        if (!isMountedRef.current || !isPollingRef.current || !monitoringId) {
            console.log('[FieldDataFetcher] Polling stopped - component unmounted or not polling');
            return;
        }

        const currentPollCount = pollCountRef.current + 1;
        pollCountRef.current = currentPollCount;

        console.log(`[FieldDataFetcher] Polling attempt ${currentPollCount}/${MAX_POLL_ATTEMPTS}`);

        try {
            const result = await apiClient.get<MonitoringDetail>(`/monitoring/${monitoringId}`);

            if (!isMountedRef.current || !isPollingRef.current) return;

            const currentCount = result?.data?.additional_metrics?.field_data_history?.length || 0;

            console.log('[FieldDataFetcher] Poll result:', {
                currentCount,
                initialCount: initialCountRef.current,
                pollCount: currentPollCount,
            });

            // Reset error counter on successful request
            consecutiveErrorsRef.current = 0;

            // Update progress in UI
            const progressFromPolling = Math.min(10 + (currentPollCount * 4), 95);
            setProgress(progressFromPolling);

            // Check if we have a new entry
            if (currentCount > initialCountRef.current) {
                // Success! New field data entry found
                console.log('[FieldDataFetcher] SUCCESS - New field data entry detected!');
                setProgress(100);
                setIsPolling(false);
                isPollingRef.current = false;
                toast.success('Field data saved successfully!');

                // Invalidate queries to refresh the UI
                queryClient.invalidateQueries({ queryKey: ['monitoredUrls'] }); // Main list with field_data_history
                queryClient.invalidateQueries({ queryKey: ['monitoringHistory'] });
                queryClient.invalidateQueries({ queryKey: ['monitoringTasks'] });
                queryClient.invalidateQueries({ queryKey: ['monitoringInsights'] });

                setTimeout(() => {
                    if (isMountedRef.current) {
                        setProgress(0);
                        onComplete?.();
                    }
                }, 1500);
                return;
            }

            // Check for timeout
            if (currentPollCount >= MAX_POLL_ATTEMPTS) {
                console.log('[FieldDataFetcher] TIMEOUT - Max poll attempts reached');
                setIsPolling(false);
                isPollingRef.current = false;
                setError('Field data fetch timed out. The data may still be processing - please refresh the page.');
                toast.error('Field data fetch is taking longer than expected. Please try refreshing the page.');
                return;
            }

            // Schedule next poll
            if (isMountedRef.current && isPollingRef.current) {
                const interval = getPollingInterval();
                console.log(`[FieldDataFetcher] Scheduling next poll in ${interval}ms`);
                pollingIntervalRef.current = setTimeout(pollMonitoringData, interval);
            }
        } catch (err: any) {
            if (!isMountedRef.current || !isPollingRef.current) return;

            console.error('[FieldDataFetcher] Polling error:', err);
            const newErrorCount = consecutiveErrorsRef.current + 1;
            consecutiveErrorsRef.current = newErrorCount;

            // Stop polling after too many consecutive errors
            if (newErrorCount >= MAX_CONSECUTIVE_ERRORS) {
                console.log('[FieldDataFetcher] FAILED - Max consecutive errors reached');
                setIsPolling(false);
                isPollingRef.current = false;
                const message = err?.response?.data?.message || err?.message || 'Failed to check field data status';
                setError(`Polling failed after ${MAX_CONSECUTIVE_ERRORS} attempts: ${message}`);
                toast.error('Unable to check field data status. Please try again.');
                return;
            }

            // Check for overall timeout
            if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
                setIsPolling(false);
                isPollingRef.current = false;
                setError('Field data fetch timed out after multiple errors.');
                return;
            }

            // Schedule next poll with increased backoff
            if (isMountedRef.current && isPollingRef.current) {
                const backoffInterval = getPollingInterval();
                console.log(`[FieldDataFetcher] Polling error, backing off to ${backoffInterval}ms`);
                pollingIntervalRef.current = setTimeout(pollMonitoringData, backoffInterval);
            }
        }
    }, [apiClient, monitoringId, queryClient, onComplete, getPollingInterval]);

    // Stop and reset
    const handleReset = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearTimeout(pollingIntervalRef.current);
        }
        isPollingRef.current = false;
        setIsPolling(false);
        setProgress(0);
        pollCountRef.current = 0;
        consecutiveErrorsRef.current = 0;
        setError(null);
    }, []);

    // Check if user can fetch field data (uses reports API)
    const canFetchFieldData = reportUsage?.canCreate?.reports ?? true;
    const cantFetchReason = reportUsage?.canCreate?.reportsReason || 'Cannot fetch field data';

    // Handle button click with canCreate check
    const handleFetchClick = () => {
        if (!canFetchFieldData) {
            toast.error(cantFetchReason);
            return;
        }
        initiateMutation.mutate();
    };

    // Compact mode: show a small button with usage info inline
    // Handle compact mode FIRST, including polling state
    if (compact) {
        const isComplete = progress >= 100;
        const hasError = !!error;

        return (
            <div className="flex items-center gap-2">
                {/* Compact usage info - show loading, usage, or polling status */}
                {isUsageLoading && !isPolling && (
                    <span className="text-xs text-muted-foreground animate-pulse">Loading...</span>
                )}
                {!isPolling && !isUsageLoading && reportUsage && (
                    <span className="text-xs text-muted-foreground">
                        {reportUsage.remainingInBundle} free {reportUsage.remainingInBundle === 1 ? 'scan' : 'scans'} remaining
                    </span>
                )}
                {/* Compact polling status */}
                {isPolling && (
                    <span className="text-xs text-muted-foreground">
                        {isComplete ? 'Scan complete!' : hasError ? 'Scan failed' : 'Scanning...'}
                    </span>
                )}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={hasError ? handleReset : handleFetchClick}
                    disabled={initiateMutation.isPending || (isPolling && !hasError) || isUsageLoading || !canFetchFieldData}
                    className="h-8 px-2 gap-1"
                    title={!canFetchFieldData ? cantFetchReason : `Rescan Field Data (${reportUsage?.remainingInBundle ?? 0} scans remaining)`}
                >
                    {initiateMutation.isPending || (isPolling && !hasError && !isComplete) ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : isComplete ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : hasError ? (
                        <AlertCircle className="h-4 w-4 text-destructive" />
                    ) : (
                        <RefreshCw className="h-4 w-4" />
                    )}
                    <span className="text-xs">
                        {isComplete ? 'Done' : hasError ? 'Retry' : 'Rescan'}
                    </span>
                </Button>
            </div>
        );
    }

    // Render based on current state - FULL UI for non-compact mode
    if (isPolling || progress > 0) {
        const isComplete = progress >= 100;
        const hasError = !!error;
        const displayPollCount = pollCountRef.current;

        return (
            <div className="flex flex-col items-center gap-4 py-4">
                <div className="flex items-center gap-2">
                    {isComplete ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : hasError ? (
                        <AlertCircle className="h-5 w-5 text-destructive" />
                    ) : (
                        <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    )}
                    <span className="text-sm font-medium">
                        {isComplete
                            ? 'Field data saved!'
                            : hasError
                                ? 'Fetch failed'
                                : progress < 30
                                    ? 'Connecting to Google PageSpeed API...'
                                    : progress < 70
                                        ? 'Fetching real user performance data...'
                                        : 'Saving field data to monitoring database...'}
                    </span>
                </div>
                <div className="w-full max-w-xs">
                    <Progress value={progress} className="h-2" />
                </div>
                <p className="text-xs text-muted-foreground">
                    {isComplete
                        ? 'Complete!'
                        : hasError
                            ? error
                            : consecutiveErrorsRef.current > 0
                                ? `Retrying with backoff... (${consecutiveErrorsRef.current} error${consecutiveErrorsRef.current > 1 ? 's' : ''})`
                                : `Checking for new data... (${displayPollCount}/${MAX_POLL_ATTEMPTS})`}
                </p>
                {hasError && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReset}
                    >
                        Try Again
                    </Button>
                )}
            </div>
        );
    }

    // Default: show the button with usage info

    return (
        <div className="flex flex-col items-center gap-4 py-4">
            <p className="text-sm text-muted-foreground text-center max-w-md">
                Field data shows real user metrics from the Chrome User Experience Report (CrUX).
                This provides actual performance data from real users visiting this page.
            </p>

            {/* Usage info display for field data */}
            <div className="text-xs text-muted-foreground text-center">
                {isUsageLoading ? (
                    <Skeleton className="h-4 w-48" />
                ) : reportUsage ? (
                    <div className="space-y-1">
                        <div>
                            <span>Scans Used: {reportUsage.consumedInPeriod}/{reportUsage.limitForPeriod}</span>
                            <span className="ml-2">Remaining: {reportUsage.remainingInBundle}</span>
                            {reportUsage.overageCostPerUnit !== null && reportUsage.overageCostPerUnit > 0 && (
                                <span className="ml-2">Overlimit: {reportUsage.overageCostPerUnit} credits/scan</span>
                            )}
                        </div>
                        {!canFetchFieldData && (
                            <p className="text-destructive">
                                {cantFetchReason}.{' '}
                                <Link to="/credits" className="underline hover:text-destructive/80">Add credits</Link>
                            </p>
                        )}
                    </div>
                ) : (
                    <span className="text-muted-foreground">Usage data unavailable</span>
                )}
            </div>

            <Button
                onClick={handleFetchClick}
                disabled={initiateMutation.isPending || isUsageLoading || !canFetchFieldData}
            >
                {initiateMutation.isPending ? (
                    <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Initiating...
                    </>
                ) : (
                    <>
                        {hasFieldData ? (
                            <RefreshCw className="mr-2 h-4 w-4" />
                        ) : (
                            <CloudLightning className="mr-2 h-4 w-4" />
                        )}
                        {hasFieldData ? 'Refresh Field Data' : 'Get Field Data'}
                    </>
                )}
            </Button>
        </div>
    );
};
